import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { Button, EmptyState, StatusBadge, ConfirmModal } from '../components/common';
import { TopicModal, TopicDetailsModal, TodoDetailsModal, EditTodoModal } from '../components/modals';
import { formatDate, isOverdue, getAgeDays } from '../utils/formatters';

/**
 * Meeting Detail page - Shows topics, sessions, and tasks for a specific meeting
 */
const MeetingDetail = ({
  meeting,
  user,
  setActiveSession,
  setView,
  onViewAllSessions,
  onViewClosedTopics,
  onViewCompletedTodos,
  onViewSummary
}) => {
  const [topics, setTopics] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [meetingTodos, setMeetingTodos] = useState([]);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [editTopic, setEditTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [editTodo, setEditTodo] = useState(null);
  const [activeSessionData, setActiveSessionData] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    // Load data immediately
    loadData();
    
    // Poll every 10 seconds to keep meeting data updated
    const pollInterval = setInterval(() => {
      loadData();
    }, 10000);
    
    // Cleanup interval on unmount
    return () => clearInterval(pollInterval);
  }, [meeting.id]);

  const loadData = async () => {
    try {
      const [topicsData, sessionsData, usersData, todosData, activeData] = await Promise.all([
        api.get(`/topics/meeting/${meeting.id}`),
        api.get(`/sessions/meeting/${meeting.id}`),
        api.get('/users/list'),
        api.get(`/todos/meeting/${meeting.id}`),
        api.get(`/sessions/meeting/${meeting.id}/active`)
      ]);
      setTopics(topicsData);
      setSessions(sessionsData);
      setUsers(usersData);
      setMeetingTodos(todosData);
      setActiveSessionData(activeData);
    } catch (error) {
      console.error('Failed to load meeting data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartMeeting = () => {
    setConfirmState({
      title: 'Start Meeting',
      message: 'Are you sure you want to start this meeting?',
      confirmLabel: 'Start Meeting',
      danger: false,
      onConfirm: async () => {
        setConfirmState(null);
        try {
          const session = await api.post('/sessions', { meetingId: meeting.id });
          setActiveSession(session);
        } catch (error) {
          console.error('Failed to start meeting:', error);
        }
      },
    });
  };

  const handleDeleteTopic = (topicId) => {
    setConfirmState({
      message: 'Delete this topic? This cannot be undone.',
      confirmLabel: 'Delete Topic',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await api.delete(`/topics/${topicId}`);
          loadData();
        } catch (error) {
          console.error('Failed to delete topic:', error);
        }
      },
    });
  };

  const handleDeleteTodo = (todoId) => {
    setConfirmState({
      message: 'Delete this task? This cannot be undone.',
      confirmLabel: 'Delete Task',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await api.delete(`/todos/${todoId}`);
          loadData();
        } catch (error) {
          console.error('Failed to delete task:', error);
        }
      },
    });
  };

  const updateStatus = async (todoId, status) => {
    try {
      await api.patch(`/todos/${todoId}/status`, { status });
      loadData();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  // Categorize topics and todos
  const openTopics = useMemo(() => topics.filter(t => !t.closed), [topics]);
  const closedTopics = useMemo(() => topics.filter(t => t.closed), [topics]);
  const incompleteTodos = useMemo(() => meetingTodos.filter(t => t.status !== 'Done'), [meetingTodos]);
  const completedTodos = useMemo(() => meetingTodos.filter(t => t.status === 'Done'), [meetingTodos]);

  if (loading) {
    return (
      <div className="page-header">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{meeting.name}</h1>
        {(user.isAdmin || user.isManager) && (
          activeSessionData ? (
            <Button variant="primary" onClick={() => setActiveSession(activeSessionData)}>
              ▶ Resume Meeting
            </Button>
          ) : (
            <Button variant="success" onClick={handleStartMeeting}>
              Start Meeting
            </Button>
          )
        )}
      </div>

      {activeSessionData && (user.isAdmin || user.isManager) && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#d4edda',
          border: '1px solid #28a745',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>
            <strong>Meeting in progress</strong> — started {formatDate(activeSessionData.startedAt)}
          </span>
          <Button variant="primary" size="small" onClick={() => setActiveSession(activeSessionData)}>
            Resume Meeting
          </Button>
        </div>
      )}

      {/* Quick Links */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        {closedTopics.length > 0 && (
          <Button variant="secondary" size="small" onClick={onViewClosedTopics}>
            View Closed Topics ({closedTopics.length})
          </Button>
        )}
        {completedTodos.length > 0 && (
          <Button variant="secondary" size="small" onClick={onViewCompletedTodos}>
            View Completed Tasks ({completedTodos.length})
          </Button>
        )}
        {sessions.length > 0 && (
          <Button variant="secondary" size="small" onClick={onViewAllSessions}>
            View Past Sessions ({sessions.length})
          </Button>
        )}
      </div>

      {/* Open Topics */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div className="flex flex-between" style={{ alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1.1rem', margin: 0, color: 'var(--color-navy)' }}>
            Open Topics ({openTopics.length})
          </h2>
          <Button
            variant="secondary"
            onClick={() => {
              setEditTopic(null);
              setShowTopicModal(true);
            }}
          >
            Add Topic
          </Button>
        </div>
        {openTopics.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No open topics. Add one to get started.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Added By</th>
                <th>Date Added</th>
                <th style={{ textAlign: 'center' }}>Age (days)</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {openTopics.map(topic => (
                <tr key={topic.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedTopic({ ...topic, meetingName: meeting.name })}>
                  <td>{topic.title}</td>
                  <td>{topic.creatorName}</td>
                  <td>{formatDate(topic.createdAt)}</td>
                  <td style={{ textAlign: 'center' }}>{getAgeDays(topic.createdAt)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {(topic.createdBy === user.id || user.isAdmin) && (
                      <div className="flex gap-1" style={{ justifyContent: 'flex-end' }}>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditTopic(topic);
                            setShowTopicModal(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="secondary"
                          size="small"
                          style={{ color: 'var(--color-danger)' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTopic(topic.id);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Incomplete Tasks */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.1rem', margin: '0 0 0.75rem 0', color: 'var(--color-navy)' }}>
          Incomplete Tasks ({incompleteTodos.length})
        </h2>
        {incompleteTodos.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No incomplete tasks.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Assigned To</th>
                <th>Due Date</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {incompleteTodos.map(todo => {
                const taskIsOverdue = isOverdue(todo.dueDate, todo.status);
                const isAssignedToUser = (todo.assignees || []).some(a => a.id === user.id);
                return (
                  <tr key={todo.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedTodo(todo)}>
                    <td>{todo.title}</td>
                    <td>
                      {(todo.assignees || []).map(a => `${a.firstName} ${a.lastName}`).join(', ') || 'Unassigned'}
                    </td>
                    <td style={{
                      color: taskIsOverdue ? 'var(--color-danger)' : 'inherit',
                      fontWeight: taskIsOverdue ? 'bold' : 'normal'
                    }}>
                      {formatDate(todo.dueDate)}
                    </td>
                    <td>
                      <StatusBadge status={todo.status} type="todo" />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {(user.isAdmin || isAssignedToUser) && (
                        <div className="flex gap-1" style={{ justifyContent: 'flex-end' }}>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditTodo(todo);
                            }}
                          >
                            Edit
                          </Button>
                          {user.isAdmin && (
                            <Button
                              variant="secondary"
                              size="small"
                              style={{ color: 'var(--color-danger)' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTodo(todo.id);
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {showTopicModal && (
        <TopicModal
          topic={editTopic}
          meetingId={meeting.id}
          onClose={() => {
            setShowTopicModal(false);
            setEditTopic(null);
          }}
          onSave={() => {
            setShowTopicModal(false);
            setEditTopic(null);
            loadData();
          }}
        />
      )}

      {selectedTopic && (
        <TopicDetailsModal
          topicId={selectedTopic.id}
          onClose={() => setSelectedTopic(null)}
        />
      )}

      {selectedTodo && (
        <TodoDetailsModal
          todoId={selectedTodo.id}
          onClose={() => setSelectedTodo(null)}
        />
      )}

      {editTodo && (
        <EditTodoModal
          todo={{
            ...editTodo,
            assignees: (editTodo.assignees || []).map(a => (typeof a === 'object' ? a.id : a)),
          }}
          users={users}
          canEdit={user.isAdmin}
          onClose={() => setEditTodo(null)}
          onSave={() => {
            setEditTodo(null);
            loadData();
          }}
        />
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger !== false}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
};

MeetingDetail.propTypes = {
  meeting: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  setActiveSession: PropTypes.func.isRequired,
  setView: PropTypes.func.isRequired,
  onViewAllSessions: PropTypes.func,
  onViewClosedTopics: PropTypes.func,
  onViewCompletedTodos: PropTypes.func,
  onViewSummary: PropTypes.func,
};

export default MeetingDetail;
